import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ArrowLeft, Shuffle, Sparkles, Save, RotateCcw, AlertTriangle, Wand2, Undo2, VenusAndMars, Trash2, Plus } from "lucide-react";
import { toast } from "sonner";
import { todayLocalIso, addDaysIso, parseLocalDate } from "@/lib/dateUtils";

const COL_W = 42;
const LANE_H = 36;
const STATUS_BLOCK = {
  draft: "bg-stone-200 border-stone-400 text-stone-800",
  confirmed: "bg-emerald-200 border-emerald-500 text-emerald-900",
  checked_in: "bg-blue-200 border-blue-500 text-blue-900",
};

const isoAddDays = (iso, n) => addDaysIso(iso, n);
const daysBetween = (a, b) => Math.round((parseLocalDate(b) - parseLocalDate(a)) / 86400000);
const genderLetter = (b) => (Number(b.party_size) !== 1 ? null : b.guest_gender === "male" ? "M" : b.guest_gender === "female" ? "F" : "?");

export default function ScrambleBoard() {
  const navigate = useNavigate();
  const today = todayLocalIso();
  const [from, setFrom] = useState(today.slice(0, 8) + "01");
  const [to, setTo] = useState(isoAddDays(today.slice(0, 8) + "01", 62));
  const [units, setUnits] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [leases, setLeases] = useState([]);
  const [staged, setStaged] = useState({});          // booking_id -> segments[]
  const [includeCheckedIn, setIncludeCheckedIn] = useState(true);
  const [loading, setLoading] = useState(true);
  const [helping, setHelping] = useState(false);
  const [saving, setSaving] = useState(false);
  const [conflictDialog, setConflictDialog] = useState(null); // {conflicts:[]}
  const [dateConfirm, setDateConfirm] = useState(null); // date-change guard
  const [overrideReason, setOverrideReason] = useState("");
  const [genderUnit, setGenderUnit] = useState(null);         // unit being edited
  const [genderRules, setGenderRules] = useState([]);
  const [savingRules, setSavingRules] = useState(false);
  const dragRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [u, b, l] = await Promise.all([
        api.get("/housing/units"),
        api.get(`/bookings?from_date=${from}&to_date=${to}`),
        api.get("/housing/leases").catch(() => ({ data: [] })),
      ]);
      setUnits((u.data || []).filter((x) => x.active !== false));
      setBookings((b.data || []).filter((x) => ["draft", "confirmed", "checked_in"].includes(x.status)));
      setLeases((l.data || []).filter((x) => ["active", "pending"].includes(x.status)));
      setStaged({});
    } catch { toast.error("Failed to load board"); }
    finally { setLoading(false); }
  }, [from, to]);
  useEffect(() => { load(); }, [load]);

  const days = useMemo(() => {
    const out = [];
    let d = from;
    let guard = 0;
    while (d <= to && guard++ < 400) { out.push(d); d = isoAddDays(d, 1); }
    return out;
  }, [from, to]);

  const dayIndex = useMemo(() => Object.fromEntries(days.map((d, i) => [d, i])), [days]);

  // Phase 71 — {`${unitId}|${date}`: "male"|"female"} for board tints
  const genderMap = useMemo(() => {
    const m = {};
    for (const u of units) {
      for (const r of (u.gender_rules || [])) {
        let d = r.from_date;
        let g = 0;
        while (d < r.to_date && g++ < 730) { m[`${u.id}|${d}`] = r.gender; d = isoAddDays(d, 1); }
      }
    }
    return m;
  }, [units]);

  const reloadUnits = async () => {
    const { data } = await api.get("/housing/units");
    setUnits((data || []).filter((x) => x.active !== false));
  };

  const saveGenderRules = async () => {
    for (const r of genderRules) {
      if (!r.gender || !r.from_date || !r.to_date || r.to_date <= r.from_date) {
        toast.error("Each rule needs a gender and a valid date range");
        return;
      }
    }
    setSavingRules(true);
    try {
      await api.put(`/housing/units/${genderUnit.id}/gender-rules`, {
        rules: genderRules.map(({ gender, from_date, to_date }) => ({ gender, from_date, to_date })),
      });
      toast.success(`${genderUnit.name} gender rules saved`);
      setGenderUnit(null);
      reloadUnits();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save rules");
    } finally { setSavingRules(false); }
  };

  const effSegments = useCallback((b) => staged[b.id] || (b.segments || []).map((s) => ({
    unit_id: s.unit_id, from_date: s.from_date, to_date: s.to_date,
  })), [staged]);

  const visibleBookings = useMemo(
    () => bookings.filter((b) => includeCheckedIn || b.status !== "checked_in" ||
      true), [bookings, includeCheckedIn]);
  // includeCheckedIn=false → checked-in still displayed but locked (handled per-block)

  const movable = (b) => includeCheckedIn || b.status !== "checked_in";

  // Build per-unit blocks + conflict map
  const { blocksByUnit, conflictCells, unhoused } = useMemo(() => {
    const byUnit = {};
    const cellCount = {}; // `${unit}|${date}` -> names[]
    const unhousedList = [];
    for (const b of visibleBookings) {
      const segs = effSegments(b).filter((s) => s.unit_id);
      if (!segs.length) { unhousedList.push(b); continue; }
      segs.forEach((s, si) => {
        if (!s.unit_id) return;
        (byUnit[s.unit_id] = byUnit[s.unit_id] || []).push({ booking: b, seg: s, segIndex: si });
        if (b.status !== "draft") {
          let d = s.from_date;
          let g = 0;
          while (d < s.to_date && g++ < 400) {
            const k = `${s.unit_id}|${d}`;
            (cellCount[k] = cellCount[k] || []).push(b.visitor_name);
            d = isoAddDays(d, 1);
          }
        }
      });
    }
    for (const l of leases) {
      if (!l.unit_id) continue;
      const lf = l.start_date, lt = l.end_date || to;
      if (lf < to && lt > from) {
        (byUnit[l.unit_id] = byUnit[l.unit_id] || []).push({ lease: l, seg: { unit_id: l.unit_id, from_date: lf, to_date: lt } });
        let d = lf < from ? from : lf;
        let g = 0;
        while (d < (lt > to ? to : lt) && g++ < 400) {
          const k = `${l.unit_id}|${d}`;
          (cellCount[k] = cellCount[k] || []).push(`lease: ${l.tenant_name}`);
          d = isoAddDays(d, 1);
        }
      }
    }
    const conflicts = new Set(Object.entries(cellCount).filter(([, v]) => v.length > 1).map(([k]) => k));
    return { blocksByUnit: byUnit, conflictCells: conflicts, unhoused: unhousedList };
  }, [visibleBookings, effSegments, leases, from, to]);

  // Lane assignment per unit (greedy interval stacking)
  const lanesByUnit = useMemo(() => {
    const out = {};
    for (const uid of Object.keys(blocksByUnit)) {
      const blocks = [...blocksByUnit[uid]].sort((a, b) => a.seg.from_date.localeCompare(b.seg.from_date));
      const laneEnds = [];
      out[uid] = blocks.map((blk) => {
        let lane = laneEnds.findIndex((end) => end <= blk.seg.from_date);
        if (lane === -1) { lane = laneEnds.length; laneEnds.push(blk.seg.to_date); }
        else laneEnds[lane] = blk.seg.to_date;
        return { ...blk, lane };
      });
    }
    return out;
  }, [blocksByUnit]);

  const stagedCount = Object.keys(staged).length;

  const stageMove = (bookingId, segIndex, newUnitId, newFromIso, newToIso = null) => {
    const b = bookings.find((x) => x.id === bookingId);
    if (!b) return;
    const segs = effSegments(b).map((s) => ({ ...s }));
    if (!segs.length) {
      const dur = Math.max(1, daysBetween(b.stay_from, b.stay_to));
      segs.push({ unit_id: newUnitId, from_date: newFromIso, to_date: newToIso || isoAddDays(newFromIso, dur) });
      setStaged((p) => ({ ...p, [bookingId]: segs }));
      return;
    }
    const s = segs[segIndex];
    if (!s) return;
    const dur = daysBetween(s.from_date, s.to_date);
    s.unit_id = newUnitId;
    s.from_date = newFromIso;
    s.to_date = newToIso || isoAddDays(newFromIso, dur);
    setStaged((p) => ({ ...p, [bookingId]: segs }));
  };

  const undoOne = (bookingId) => setStaged((p) => {
    const n = { ...p }; delete n[bookingId]; return n;
  });

  const onDrop = (e, unitId, dateIso) => {
    e.preventDefault();
    const raw = dragRef.current;
    if (!raw) return;
    const { bookingId, segIndex, grabOffset } = raw;
    dragRef.current = null;
    const newFrom = isoAddDays(dateIso, -grabOffset);
    // Date-change guard: moving house is fine, but if the drop also shifts
    // the dates, confirm before staging (with quick calendar correction).
    const b = bookings.find((x) => x.id === bookingId);
    const cur = b ? effSegments(b)[segIndex] : null;
    if (cur && newFrom !== cur.from_date) {
      const dur = daysBetween(cur.from_date, cur.to_date);
      setDateConfirm({
        bookingId, segIndex, unitId,
        visitor: b.visitor_name,
        origFrom: cur.from_date, origTo: cur.to_date,
        newFrom, newTo: isoAddDays(newFrom, dur),
      });
      return;
    }
    stageMove(bookingId, segIndex, unitId, newFrom);
  };

  const helpMe = async (mode) => {
    setHelping(true);
    try {
      const { data } = await api.post("/bookings/scramble/recommend", {
        from_date: from, to_date: to, mode, include_checked_in: includeCheckedIn,
      });
      const next = {};
      for (const p of data.proposals || []) {
        if (p.changed) next[p.booking_id] = p.segments;
      }
      setStaged((prev) => ({ ...prev, ...next }));
      const s = data.summary || {};
      if (s.changed === 0) toast.success("Everything already fits — no moves recommended");
      else toast.success(`Recommendation staged: ${s.changed} booking(s) moved${s.unhoused ? `, ${s.unhoused} with unhoused nights` : ""} — review then Save`);
      if (s.unhoused > 0) toast.warning(`${s.unhoused} booking(s) could not be fully housed — check the Unhoused strip`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Recommendation failed");
    } finally { setHelping(false); }
  };

  const doSave = async (allowOverride) => {
    setSaving(true);
    try {
      const changes = Object.entries(staged).map(([booking_id, segments]) => ({ booking_id, segments }));
      const { data } = await api.post("/bookings/scramble/apply", {
        changes, allow_override: allowOverride, override_reason: overrideReason,
      });
      toast.success(`Saved — ${data.updated} booking(s) re-housed${data.conflicts_overridden ? `, ${data.conflicts_overridden} conflict-night(s) shared` : ""}`);
      setConflictDialog(null);
      setOverrideReason("");
      load();
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (e.response?.status === 409 && detail?.conflicts) {
        setConflictDialog(detail);
      } else {
        toast.error(typeof detail === "string" ? detail : "Save failed");
      }
    } finally { setSaving(false); }
  };

  const unitRowHeight = (uid) => {
    const lanes = Math.max(1, ...(lanesByUnit[uid] || []).map((b) => b.lane + 1));
    return lanes * LANE_H + 8;
  };

  return (
    <div className="space-y-4 fade-in" data-testid="scramble-board">
      <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-sm text-[#525C58] hover:text-[#1B4332]" data-testid="scramble-back">
        <ArrowLeft className="h-4 w-4" /> Back
      </button>
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <h1 className="text-4xl font-semibold tracking-tight flex items-center gap-3" style={{ fontFamily: "Manrope, sans-serif" }}>
            <Shuffle className="h-8 w-8 text-[#1B4332]" /> Scramble
          </h1>
          <p className="text-[#525C58] text-sm mt-1">Drag guests between houses and dates. Nothing is saved until you hit Save.</p>
        </div>
        <div className="flex items-end gap-2 ml-auto flex-wrap">
          <div className="space-y-1"><Label className="text-xs">From</Label>
            <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="h-9 w-36" data-testid="scramble-from" /></div>
          <div className="space-y-1"><Label className="text-xs">To</Label>
            <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="h-9 w-36" data-testid="scramble-to" /></div>
          <label className="flex items-center gap-2 text-xs text-[#525C58] h-9 px-2 rounded-md border border-[#E8E8E6] cursor-pointer">
            <input type="checkbox" checked={includeCheckedIn} onChange={(e) => setIncludeCheckedIn(e.target.checked)}
                   data-testid="scramble-include-checkedin" />
            Move current guests too
          </label>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" disabled={helping} className="h-9 border-amber-400 text-amber-800 hover:bg-amber-50" data-testid="help-me-btn">
                <Sparkles className="h-4 w-4 mr-1" /> {helping ? "Thinking…" : "HELP ME"}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => helpMe("fix")} data-testid="help-me-fix">
                <Wand2 className="h-4 w-4 mr-2" /> Fix problems only — house the unhoused &amp; resolve clashes
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => helpMe("reshuffle")} data-testid="help-me-reshuffle">
                <Shuffle className="h-4 w-4 mr-2" /> Full reshuffle — best fit for everyone in range
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button variant="outline" onClick={() => setStaged({})} disabled={!stagedCount} className="h-9" data-testid="scramble-reset">
            <RotateCcw className="h-4 w-4 mr-1" /> Reset
          </Button>
          <Button onClick={() => doSave(false)} disabled={!stagedCount || saving}
                  className="h-9 bg-[#1B4332] hover:bg-[#2D6A4F]" data-testid="scramble-save">
            <Save className="h-4 w-4 mr-1" /> {saving ? "Saving…" : `Save all changes${stagedCount ? ` (${stagedCount})` : ""}`}
          </Button>
        </div>
      </div>

      {unhoused.length > 0 && (
        <div className="rounded-lg border border-amber-300 bg-amber-50/70 p-3" data-testid="unhoused-strip">
          <p className="text-xs font-semibold text-amber-900 mb-2 flex items-center gap-1">
            <AlertTriangle className="h-3.5 w-3.5" /> Unhoused in this range — drag onto the board or use HELP ME
          </p>
          <div className="flex flex-wrap gap-2">
            {unhoused.map((b) => (
              <span key={b.id} draggable={movable(b)}
                    onDragStart={(e) => {
                      e.dataTransfer.setData("text/plain", b.id);
                      dragRef.current = { bookingId: b.id, segIndex: 0, grabOffset: 0 };
                    }}
                    className="px-2 py-1 rounded border border-amber-400 bg-white text-xs cursor-grab"
                    data-testid={`unhoused-${b.id}`}>
                {b.visitor_name} {genderLetter(b) ? `(${genderLetter(b)})` : ""} · {b.party_size} pax · {b.stay_from}→{b.stay_to}
              </span>
            ))}
          </div>
        </div>
      )}

      {loading ? <div className="py-16 text-center text-[#7A8A84]">Loading board…</div> : (
        <div className="overflow-auto border border-[#E8E8E6] rounded-lg bg-white" style={{ maxHeight: "70vh" }}>
          <div style={{ minWidth: 180 + days.length * COL_W }}>
            {/* Header row */}
            <div className="flex sticky top-0 z-20 bg-[#F4F4F1] border-b border-[#E8E8E6]">
              <div className="sticky left-0 z-30 bg-[#F4F4F1] border-r border-[#E8E8E6] px-3 py-2 text-xs font-semibold" style={{ width: 180, minWidth: 180 }}>
                House / unit
              </div>
              {days.map((d) => {
                const dt = parseLocalDate(d);
                const isMonthStart = d.endsWith("-01");
                return (
                  <div key={d} className={`text-center border-r border-[#EFEFEC] py-1 ${dt.getDay() === 0 || dt.getDay() === 6 ? "bg-[#ECEEE9]" : ""} ${isMonthStart ? "border-l-2 border-l-[#1B4332]/40" : ""}`}
                       style={{ width: COL_W, minWidth: COL_W }}>
                    <div className="text-[9px] text-[#7A8A84] uppercase">{dt.toLocaleDateString(undefined, { weekday: "narrow" })}</div>
                    <div className="text-[11px] font-medium">{dt.getDate()}</div>
                    {(isMonthStart || dayIndex[d] === 0) && <div className="text-[8px] text-[#1B4332] font-semibold uppercase">{dt.toLocaleDateString(undefined, { month: "short" })}</div>}
                  </div>
                );
              })}
            </div>
            {/* Unit rows */}
            {units.map((u) => {
              const rowH = unitRowHeight(u.id);
              return (
                <div key={u.id} className="flex border-b border-[#EFEFEC]" data-testid={`scramble-row-${u.id}`}>
                  <div className="sticky left-0 z-10 bg-white border-r border-[#E8E8E6] px-3 py-2" style={{ width: 180, minWidth: 180 }}>
                    <p className="text-sm font-medium text-[#1C1F1E]">{u.name}</p>
                    <p className="text-[10px] text-[#7A8A84]">sleeps {u.capacity}</p>
                    <div className="flex items-center gap-1 mt-0.5 flex-wrap">
                      {(u.gender_rules || []).map((r) => (
                        <span key={r.id}
                              className={`text-[9px] px-1 rounded border font-semibold ${r.gender === "male" ? "bg-sky-100 text-sky-800 border-sky-300" : "bg-rose-100 text-rose-800 border-rose-300"}`}
                              title={`${r.gender === "male" ? "Male" : "Female"} only ${r.from_date} → ${r.to_date}`}>
                        {r.gender === "male" ? "M" : "F"} {r.from_date.slice(5)}→{r.to_date.slice(5)}
                        </span>
                      ))}
                      <button onClick={() => { setGenderUnit(u); setGenderRules((u.gender_rules || []).map((r) => ({ ...r }))); }}
                              className="text-[#7A8A84] hover:text-[#1B4332]" title="Gender restrictions for this unit"
                              data-testid={`gender-rules-btn-${u.id}`}>
                        <VenusAndMars className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                  <div className="relative" style={{ width: days.length * COL_W, height: rowH }}>
                    {/* drop cells */}
                    {days.map((d) => {
                      const gm = genderMap[`${u.id}|${d}`];
                      return (
                        <div key={d}
                             onDragOver={(e) => e.preventDefault()}
                             onDrop={(e) => onDrop(e, u.id, d)}
                             className={`absolute top-0 bottom-0 border-r border-[#F3F3F0] ${conflictCells.has(`${u.id}|${d}`) ? "bg-red-100/70" : gm === "male" ? "bg-sky-100/60" : gm === "female" ? "bg-rose-100/60" : ""}`}
                             style={{ left: dayIndex[d] * COL_W, width: COL_W }}
                             data-testid={`cell-${u.id}-${d}`} />
                      );
                    })}
                    {/* blocks */}
                    {(lanesByUnit[u.id] || []).map((blk, i) => {
                      const s = blk.seg;
                      const startIdx = Math.max(0, dayIndex[s.from_date] ?? (s.from_date < from ? 0 : days.length));
                      const endIdx = s.to_date > to ? days.length : (dayIndex[s.to_date] ?? days.length);
                      if (endIdx <= 0 || startIdx >= days.length) return null;
                      const width = Math.max(1, endIdx - startIdx) * COL_W - 4;
                      if (blk.lease) {
                        return (
                          <div key={`lease-${i}`}
                               className="absolute rounded border bg-gray-300/80 border-gray-400 text-gray-700 text-[10px] px-1.5 flex items-center overflow-hidden"
                               style={{ left: startIdx * COL_W + 2, top: blk.lane * LANE_H + 4, width, height: LANE_H - 8 }}
                               title={`Lease — ${blk.lease.tenant_name} (${s.from_date} → ${s.to_date || "open-ended"}) — not movable here`}>
                            🔒 {blk.lease.tenant_name}
                          </div>
                        );
                      }
                      const b = blk.booking;
                      const isStaged = !!staged[b.id];
                      const locked = !movable(b);
                      return (
                        <div key={`${b.id}-${blk.segIndex}`}
                             draggable={!locked}
                             onDragStart={(e) => {
                               e.dataTransfer.setData("text/plain", b.id);
                               const rect = e.currentTarget.getBoundingClientRect();
                               const grabOffset = Math.floor((e.clientX - rect.left) / COL_W);
                               dragRef.current = { bookingId: b.id, segIndex: blk.segIndex, grabOffset };
                             }}
                             className={`absolute rounded border-2 text-[10px] px-1.5 flex items-center gap-1 overflow-hidden select-none
                                         ${locked ? "cursor-not-allowed opacity-70" : "cursor-grab active:cursor-grabbing"}
                                         ${STATUS_BLOCK[b.status] || STATUS_BLOCK.draft}
                                         ${isStaged ? "ring-2 ring-amber-500 ring-offset-1" : ""}`}
                             style={{ left: startIdx * COL_W + 2, top: blk.lane * LANE_H + 4, width, height: LANE_H - 8 }}
                             title={`${b.visitor_name} · ${b.party_size} pax · ${s.from_date} → ${s.to_date} (${b.status})${isStaged ? " — MOVED (unsaved)" : ""}${locked ? " — locked (current guest)" : ""}`}
                             data-testid={`block-${b.id}-${blk.segIndex}`}>
                          <span className="truncate font-medium">{b.visitor_name}</span>
                          <span className="opacity-70 shrink-0">{b.party_size}p</span>
                          {genderLetter(b) && (
                            <span className={`shrink-0 px-0.5 rounded text-[8px] font-bold border ${b.guest_gender === "male" ? "bg-sky-50 text-sky-800 border-sky-300" : b.guest_gender === "female" ? "bg-rose-50 text-rose-800 border-rose-300" : "bg-amber-50 text-amber-800 border-amber-300"}`}
                                  title={b.guest_gender ? b.guest_gender : "gender not recorded"}>
                              {genderLetter(b)}
                            </span>
                          )}
                          {isStaged && (
                            <button onClick={(e) => { e.stopPropagation(); undoOne(b.id); }}
                                    className="ml-auto shrink-0 hover:text-red-700" title="Undo this booking's staged move"
                                    data-testid={`undo-${b.id}`}>
                              <Undo2 className="h-3 w-3" />
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-4 text-[11px] text-[#525C58]">
        <span className="flex items-center gap-1"><span className="h-3 w-3 rounded bg-emerald-200 border border-emerald-500 inline-block" /> Confirmed</span>
        <span className="flex items-center gap-1"><span className="h-3 w-3 rounded bg-blue-200 border border-blue-500 inline-block" /> Checked-in</span>
        <span className="flex items-center gap-1"><span className="h-3 w-3 rounded bg-stone-200 border border-stone-400 inline-block" /> Draft</span>
        <span className="flex items-center gap-1"><span className="h-3 w-3 rounded bg-gray-300 border border-gray-400 inline-block" /> Lease (fixed)</span>
        <span className="flex items-center gap-1"><span className="h-3 w-3 rounded bg-red-100 border border-red-300 inline-block" /> Double-booked night</span>
        <span className="flex items-center gap-1"><span className="h-3 w-3 rounded bg-sky-100 border border-sky-300 inline-block" /> Male-only period</span>
        <span className="flex items-center gap-1"><span className="h-3 w-3 rounded bg-rose-100 border border-rose-300 inline-block" /> Female-only period</span>
        <span className="flex items-center gap-1"><span className="h-3 w-3 rounded ring-2 ring-amber-500 inline-block" /> Moved (unsaved)</span>
      </div>

      {/* Gender rules dialog */}
      <Dialog open={!!genderUnit} onOpenChange={(o) => !o && setGenderUnit(null)}>
        <DialogContent className="max-w-lg" data-testid="gender-rules-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <VenusAndMars className="h-5 w-5 text-[#1B4332]" /> Gender restrictions — {genderUnit?.name}
            </DialogTitle>
            <DialogDescription>
              During a restricted period only single guests of that gender can be housed here.
              HELP ME respects these rules; manual moves that break them show up as conflicts at save.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            {genderRules.length === 0 && (
              <p className="text-sm text-[#7A8A84]" data-testid="no-gender-rules">No restrictions — any guest can stay here.</p>
            )}
            {genderRules.map((r, i) => (
              <div key={i} className="flex items-end gap-2" data-testid={`gender-rule-row-${i}`}>
                <div className="space-y-1 w-32">
                  <Label className="text-xs">Only</Label>
                  <Select value={r.gender} onValueChange={(v) => setGenderRules((p) => p.map((x, xi) => xi === i ? { ...x, gender: v } : x))}>
                    <SelectTrigger className="h-9" data-testid={`gender-rule-gender-${i}`}><SelectValue placeholder="Gender" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="male">Male only</SelectItem>
                      <SelectItem value="female">Female only</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1 flex-1">
                  <Label className="text-xs">From</Label>
                  <Input type="date" className="h-9" value={r.from_date}
                         onChange={(e) => setGenderRules((p) => p.map((x, xi) => xi === i ? { ...x, from_date: e.target.value } : x))}
                         data-testid={`gender-rule-from-${i}`} />
                </div>
                <div className="space-y-1 flex-1">
                  <Label className="text-xs">Until</Label>
                  <Input type="date" className="h-9" value={r.to_date}
                         onChange={(e) => setGenderRules((p) => p.map((x, xi) => xi === i ? { ...x, to_date: e.target.value } : x))}
                         data-testid={`gender-rule-to-${i}`} />
                </div>
                <Button variant="ghost" size="sm" className="text-red-600 h-9"
                        onClick={() => setGenderRules((p) => p.filter((_, xi) => xi !== i))}
                        data-testid={`gender-rule-delete-${i}`}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
            <Button variant="outline" size="sm"
                    onClick={() => setGenderRules((p) => [...p, { gender: "", from_date: from, to_date: to }])}
                    data-testid="add-gender-rule-btn">
              <Plus className="h-4 w-4 mr-1" /> Add restriction
            </Button>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setGenderUnit(null)} data-testid="gender-rules-cancel">Cancel</Button>
            <Button onClick={saveGenderRules} disabled={savingRules} className="bg-[#1B4332] hover:bg-[#2D6A4F]"
                    data-testid="gender-rules-save">
              {savingRules ? "Saving…" : "Save rules"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Conflict override dialog */}
      <Dialog open={!!conflictDialog} onOpenChange={(o) => !o && setConflictDialog(null)}>
        <DialogContent className="max-w-lg" data-testid="scramble-conflict-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <AlertTriangle className="h-5 w-5" /> Double bookings detected
            </DialogTitle>
            <DialogDescription>{conflictDialog?.message}</DialogDescription>
          </DialogHeader>
          <div className="max-h-56 overflow-y-auto rounded-lg border border-red-200 bg-red-50/50 p-3 space-y-1">
            {(conflictDialog?.conflicts || []).map((c, i) => (
              <p key={i} className="text-xs text-red-800" data-testid={`conflict-line-${i}`}>{c}</p>
            ))}
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Override reason (room sharing)</Label>
            <Input value={overrideReason} onChange={(e) => setOverrideReason(e.target.value)}
                   placeholder="e.g. families agreed to share Guest House A" data-testid="override-reason-input" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConflictDialog(null)} data-testid="conflict-cancel">Go back &amp; adjust</Button>
            <Button onClick={() => doSave(true)} disabled={saving} className="bg-purple-700 hover:bg-purple-800"
                    data-testid="conflict-override-save">
              {saving ? "Saving…" : "Override & save with room sharing"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Date-change confirmation (drag changed the dates, not just the house) */}
      <Dialog open={!!dateConfirm} onOpenChange={(o) => !o && setDateConfirm(null)}>
        <DialogContent className="max-w-md" data-testid="date-confirm-dialog">
          <DialogHeader>
            <DialogTitle>Dates changed — confirm</DialogTitle>
            <DialogDescription>
              {dateConfirm && (
                <>Moving <b>{dateConfirm.visitor}</b> also changes their dates from{" "}
                <b>{dateConfirm.origFrom} → {dateConfirm.origTo}</b> to{" "}
                <b>{dateConfirm.newFrom} → {dateConfirm.newTo}</b>. Keep the original dates, or adjust below.</>
              )}
            </DialogDescription>
          </DialogHeader>
          {dateConfirm && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-[#525C58]">Arrival</label>
                <Input type="date" value={dateConfirm.newFrom}
                       onChange={(e) => setDateConfirm({ ...dateConfirm, newFrom: e.target.value })}
                       data-testid="date-confirm-from" />
              </div>
              <div>
                <label className="text-xs text-[#525C58]">Departure</label>
                <Input type="date" value={dateConfirm.newTo}
                       onChange={(e) => setDateConfirm({ ...dateConfirm, newTo: e.target.value })}
                       data-testid="date-confirm-to" />
              </div>
            </div>
          )}
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setDateConfirm(null)} data-testid="date-confirm-cancel">Cancel move</Button>
            <Button variant="outline"
                    className="border-[#1B4332]/30 text-[#1B4332]"
                    onClick={() => {
                      stageMove(dateConfirm.bookingId, dateConfirm.segIndex, dateConfirm.unitId,
                                dateConfirm.origFrom, dateConfirm.origTo);
                      setDateConfirm(null);
                      toast.success("Moved house — original dates kept");
                    }}
                    data-testid="date-confirm-keep">
              Keep previous dates
            </Button>
            <Button className="bg-[#1B4332] hover:bg-[#2D6A4F]"
                    disabled={dateConfirm && dateConfirm.newTo <= dateConfirm.newFrom}
                    onClick={() => {
                      stageMove(dateConfirm.bookingId, dateConfirm.segIndex, dateConfirm.unitId,
                                dateConfirm.newFrom, dateConfirm.newTo);
                      setDateConfirm(null);
                    }}
                    data-testid="date-confirm-apply">
              Apply new dates
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
